import type { UnityLoadPhase } from "@/lib/unity/load-lifecycle";
import { isAbortError } from "@/lib/unity/retry";

export const UNITY_GAME_LOAD_FAILED_EVENT = "unity_game_load_failed";
export const UNITY_GAME_LOADED_EVENT = "unity_game_loaded";
export const UNITY_GAME_LOAD_ABORTED_EVENT = "unity_game_load_aborted";

export type UnityGameLoadFailureSource = "load_exception" | "unity_runtime_error";

export type UnityGameLoadFailurePhase =
  | "canvas_setup"
  | "file_list_fetch"
  | "build_file_detection"
  | "loader_script_injection"
  | "unity_instance_creation"
  | "unity_runtime"
  | "unknown";

/**
 * Normalized bucket for the known Unity load-failure signatures so the opaque
 * catch-all is classified, not just messaged. Derived from the failure message
 * since the underlying errors are not joinable to $exceptions.
 */
export type UnityGameLoadFailureReason =
  | "webgl_context_loss"
  | "unsupported_webgl"
  | "wasm_missing_function"
  | "out_of_memory"
  | "audio_decode"
  | "network"
  | "permission"
  | "missing_build_files"
  | "unknown";

export type UnityGameLoadFailureContext = {
  game_id: string;
  game_slug: string;
  game_name: string;
  asset_id: string;
  version_id: string;
};

type CaptureFn = (event: string, properties?: Record<string, unknown>) => void;

type UnityGameLoadFailureDetails = {
  failurePhase?: UnityGameLoadFailurePhase;
  elapsedMs?: number;
  wasBackgrounded?: boolean;
};

/**
 * Subset of `navigator`/`NetworkInformation` fields used for load-failure
 * diagnostics. These are non-standard / not yet in the DOM lib types and are
 * absent on some browsers, so every field is optional.
 */
type NetworkInformationLike = {
  effectiveType?: string;
  downlink?: number;
  rtt?: number;
};

type NavigatorWithDiagnostics = Navigator & {
  deviceMemory?: number;
  connection?: NetworkInformationLike;
};

function getDefaultFailurePhase(
  failureSource: UnityGameLoadFailureSource
): UnityGameLoadFailurePhase {
  return failureSource === "unity_runtime_error" ? "unity_runtime" : "unknown";
}

function normalizeFailureMessage(errorMessage: string): string {
  return errorMessage.trim() || "Failed to load game";
}

function normalizeElapsedMs(elapsedMs: number | undefined): number {
  if (typeof elapsedMs !== "number" || !Number.isFinite(elapsedMs)) {
    return 0;
  }

  return Math.max(0, Math.round(elapsedMs));
}

function messageIncludesAny(text: string, needles: readonly string[]): boolean {
  return needles.some((needle) => text.includes(needle));
}

const FAILURE_REASON_SIGNATURES: ReadonlyArray<{
  reason: UnityGameLoadFailureReason;
  needles: readonly string[];
}> = [
  { reason: "permission", needles: ["permission", "sign in"] },
  {
    reason: "webgl_context_loss",
    needles: ["glctx", "webgl context", "context lost", "lost context"],
  },
  {
    reason: "unsupported_webgl",
    needles: ["does not support webgl", "does not support graphics api"],
  },
  {
    reason: "out_of_memory",
    needles: [
      "subarray",
      "out of memory",
      "enlarge memory",
      "array buffer allocation failed",
      "rangeerror",
    ],
  },
  {
    reason: "wasm_missing_function",
    needles: ["null function or function signature mismatch", "table index is out of bounds"],
  },
  { reason: "audio_decode", needles: ["encodingerror", "decodeaudiodata"] },
  {
    reason: "missing_build_files",
    needles: ["not found", "no unity loader", "createunityinstance not available"],
  },
  {
    reason: "network",
    needles: [
      "load failed",
      "failed to fetch",
      "networkerror",
      "failed to load unity loader script",
      "failed to fetch file list",
      "failed to list files",
      "too many requests",
      "both async and sync fetching of the wasm failed",
      "unable to load file",
    ],
  },
];

/**
 * Buckets a raw failure message into a known signature so the dominant
 * catch-all becomes diagnosable. Order matters: more specific signatures are
 * checked before broader ones (e.g. memory before generic network).
 */
export function classifyFailureReason(message: string): UnityGameLoadFailureReason {
  const text = message.toLowerCase();
  const match = FAILURE_REASON_SIGNATURES.find((signature) =>
    messageIncludesAny(text, signature.needles)
  );
  return match?.reason ?? "unknown";
}

function setIfNumber(env: Record<string, unknown>, key: string, value: unknown): void {
  if (typeof value === "number" && Number.isFinite(value)) {
    env[key] = value;
  }
}

function collectDeviceInfo(env: Record<string, unknown>, nav: NavigatorWithDiagnostics): void {
  setIfNumber(env, "device_memory", nav.deviceMemory);
  setIfNumber(env, "hardware_concurrency", nav.hardwareConcurrency);
}

function collectNetworkInfo(env: Record<string, unknown>, nav: NavigatorWithDiagnostics): void {
  const connection = nav.connection;
  if (!connection) return;

  if (typeof connection.effectiveType === "string") {
    env.effective_connection_type = connection.effectiveType;
  }
  setIfNumber(env, "downlink", connection.downlink);
  setIfNumber(env, "rtt", connection.rtt);
}

/**
 * Collects best-effort device / network / visibility signal at failure time.
 * Fields the browser does not expose are omitted rather than emitted as null,
 * so downstream "present on supporting browsers" metrics stay meaningful.
 */
function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === "string") {
    return error;
  }
  return "";
}

/**
 * Transient fetch/CDN faults are worth retrying. Permission, missing builds,
 * unsupported GPUs, and abort are not.
 */
const TRANSIENT_HTTP_STATUSES = new Set([408, 429, 500, 502, 503, 504]);

function getErrorHttpStatus(error: unknown): number | undefined {
  if (!error || typeof error !== "object" || !("status" in error)) {
    return undefined;
  }
  const status = (error as { status?: unknown }).status;
  return typeof status === "number" && Number.isFinite(status) ? status : undefined;
}

export function isTransientUnityLoadError(error: unknown): boolean {
  if (isAbortError(error)) {
    return false;
  }
  if (error instanceof Error && (error as { isPermissionDenied?: boolean }).isPermissionDenied) {
    return false;
  }
  const status = getErrorHttpStatus(error);
  if (status !== undefined) {
    return TRANSIENT_HTTP_STATUSES.has(status);
  }
  return classifyFailureReason(getErrorMessage(error)) === "network";
}

function collectClientEnvironment(): Record<string, unknown> {
  const env: Record<string, unknown> = {};

  if (typeof navigator !== "undefined") {
    const nav = navigator as NavigatorWithDiagnostics;
    collectDeviceInfo(env, nav);
    collectNetworkInfo(env, nav);
  }

  if (typeof document !== "undefined") {
    env.visibility_state = document.visibilityState;
  }

  return env;
}

type UnityLoadTimingDetails = {
  elapsedMs?: number;
  wasBackgrounded?: boolean;
};

function buildUnityLoadEventProperties(
  context: UnityGameLoadFailureContext,
  details: UnityLoadTimingDetails = {}
): Record<string, unknown> {
  return {
    ...context,
    elapsed_ms: normalizeElapsedMs(details.elapsedMs),
    was_backgrounded: details.wasBackgrounded ?? false,
    ...collectClientEnvironment(),
    page_path: typeof window !== "undefined" ? window.location.pathname : "",
  };
}

/**
 * Reports a Unity WebGL load or startup failure to PostHog (or any capture-compatible client).
 */
export function captureUnityGameLoadFailure(
  capture: CaptureFn | undefined,
  context: UnityGameLoadFailureContext,
  errorMessage: string,
  failureSource: UnityGameLoadFailureSource,
  details: UnityGameLoadFailureDetails = {}
): void {
  if (!capture) return;

  const failureMessage = normalizeFailureMessage(errorMessage);

  capture(UNITY_GAME_LOAD_FAILED_EVENT, {
    ...buildUnityLoadEventProperties(context, details),
    failure_source: failureSource,
    error_message: failureMessage,
    failure_message: failureMessage,
    failure_phase: details.failurePhase ?? getDefaultFailurePhase(failureSource),
    failure_reason: classifyFailureReason(failureMessage),
  });
}

export function captureUnityGameLoaded(
  capture: CaptureFn | undefined,
  context: UnityGameLoadFailureContext,
  details: UnityLoadTimingDetails = {}
): void {
  if (!capture) return;

  capture(UNITY_GAME_LOADED_EVENT, buildUnityLoadEventProperties(context, details));
}

export function captureUnityGameLoadAborted(
  capture: CaptureFn | undefined,
  context: UnityGameLoadFailureContext,
  details: UnityLoadTimingDetails & { abortPhase: UnityLoadPhase }
): void {
  if (!capture) return;

  capture(UNITY_GAME_LOAD_ABORTED_EVENT, {
    ...buildUnityLoadEventProperties(context, details),
    abort_phase: details.abortPhase,
  });
}
